import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./../Styles/designdetails.css";
import { API } from "../../config/api";
import VerticalAdsDis from "../../Admin/Pages/VerticleAdsDis";
import HorizontalAds from "../../Admin/Pages/HorizontalAds";

export default function SellDesignDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const design = location.state;

  const [isAddedCart, setIsAddedCart] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [concepts, setConcepts] = useState([]);

  const [zoomData, setZoomData] = useState({
    show: false,
    x: 0,
    y: 0,
    xP: 0,
    yP: 0,
  });

  // VERIFY IMAGE URL
  const imageUrl = design
    ? `${API.BASE_URL}/uploads/Design_seller/${design.user_id}/${design.jpeg_file}`
    : "";

  const fetchConcepts = async () => {
    try {
      const res = await fetch(API.CONCEPT_LIST);
      const data = await res.json();
      if (data.status) setConcepts(data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const syncStatus = useCallback(() => {
    const loginUser = JSON.parse(sessionStorage.getItem("user"));
    if (!loginUser || !design) return;

    const wishlist =
      JSON.parse(localStorage.getItem(`wishlist_${loginUser.id}`)) || [];
    const cart = JSON.parse(localStorage.getItem(`cart_${loginUser.id}`)) || [];

    setIsWishlisted(
      wishlist.some((item) => item.id === design.id && item.type === "design"),
    );

    setIsAddedCart(
      cart.some((item) => item.id === design.id && item.type === "design"),
    );
  }, [design]);

  useEffect(() => {
    fetchConcepts();
    syncStatus();

    window.addEventListener("cartUpdated", syncStatus);
    window.addEventListener("storage", syncStatus);

    return () => {
      window.removeEventListener("cartUpdated", syncStatus);
      window.removeEventListener("storage", syncStatus);
    };
  }, [syncStatus]);

  const handleMouseMove = (e) => {
    const container = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - container.left;
    const y = e.clientY - container.top;

    const xPercent = (x / container.width) * 100;
    const yPercent = (y / container.height) * 100;

    setZoomData({ show: true, x, y, xP: xPercent, yP: yPercent });
  };

  const toggleCart = () => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user) return alert("Please Login First");

    let items = JSON.parse(localStorage.getItem(`cart_${user.id}`)) || [];
    const exists = items.find((i) => i.id === design.id && i.type === "design");

    if (exists) {
      items = items.filter((i) => !(i.id === design.id && i.type === "design"));
      setIsAddedCart(false);
    } else {
      items.push({ ...design, type: "design" });
      setIsAddedCart(true);
    }

    localStorage.setItem(`cart_${user.id}`, JSON.stringify(items));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const toggleWishlist = () => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (!user) return alert("Please Login First");

    let items = JSON.parse(localStorage.getItem(`wishlist_${user.id}`)) || [];
    const exists = items.find((i) => i.id === design.id && i.type === "design");

    if (exists) {
      items = items.filter((i) => !(i.id === design.id && i.type === "design"));
      setIsWishlisted(false);
    } else {
      items.push({ ...design, type: "design" });
      setIsWishlisted(true);
    }

    localStorage.setItem(`wishlist_${user.id}`, JSON.stringify(items));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  if (!design)
    return <div className="loading-spinner">Loading design details...</div>;

  return (
    <div className="design-details-container">
      <button
        className="btn-back"
        onClick={() =>
          navigate("/designs", {
            state: { activeTab: design?.fromTab || "paid" },
          })
        }
      >
        <i className="bi bi-arrow-left me-1"></i> Back to Designs
      </button>

      <div className="row g-4">
        {/* LEFT COLUMN: MAIN IMAGE */}
        <div className="col-lg-4 pe-4">
          <div
            className="details-image-wrapper shadow-sm p-0"
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setZoomData((p) => ({ ...p, show: true }))}
            onMouseLeave={() => setZoomData((p) => ({ ...p, show: false }))}
          >
            <img
              src={imageUrl}
              alt={design.design_code}
              className="main-design-img"
            />
            {zoomData.show && (
              <div
                className="zoom-viewfinder"
                style={{
                  left: `${zoomData.x - 75}px`,
                  top: `${zoomData.y - 75}px`,
                }}
              />
            )}
          </div>
        </div>

        {/* MIDDLE COLUMN: DETAILS & ACTIONS OR ZOOM VIEW */}
        <div className="col-lg-6 pe-4">
          {zoomData.show ? (
            <div
              className="zoom-result-window shadow-sm"
              style={{
                backgroundImage: `url("${imageUrl}")`,
                backgroundSize: "120%",
                backgroundPosition: `${zoomData.xP}% ${zoomData.yP}%`,
              }}
            />
          ) : (
            <div className="details-content">
              <span className="category-badge">{design.category}</span>
              <h1 className="design-code-title">
                Design Code: <span>{design.design_code}</span>
              </h1>
              <h3 className="price-tag">₹ {design.price}</h3>

              <div className="specs-grid">
                <div className="spec-row">
                  <strong>Stitches:</strong> <span>{design.stitch}</span>
                </div>
                <div className="spec-row">
                  <strong>Area:</strong> <span>{design.area}</span>
                </div>

                <div className="spec-row">
                  <strong>Height:</strong> <span>{design.height}</span>
                </div>
                <div className="spec-row">
                  <strong>Width:</strong> <span>{design.width}</span>
                </div>
                <div className="spec-row">
                  <strong>Colours:</strong> <span>{design.color}</span>
                </div>
                <div className="spec-row">
                  <strong>Format:</strong> <span>.EMB</span>
                </div>
                <div className="spec-row full-width">
                  <strong>Concept:</strong>{" "}
                  <span>
                    {design.concepts_array
                      ?.map(
                        (id) => concepts.find((c) => c.id === id)?.concept_name,
                      )
                      .filter(Boolean)
                      .join(", ") || ""}
                  </span>
                </div>
                <div className="spec-row full-width">
                  <strong>Details:</strong> <span>{design.details || ""}</span>
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="action-buttons-wrapper">
                <button
                  className={`btn-action ${
                    isAddedCart ? "btn-cart-added" : "btn-cart-add"
                  }`}
                  onClick={toggleCart}
                >
                  <i
                    className={`bi ${
                      isAddedCart ? "bi-cart-check-fill" : "bi-cart-fill"
                    } me-2`}
                  ></i>
                  {isAddedCart ? "ADDED TO CART" : "SELECT DESIGN"}
                </button>

                <button
                  className={`btn-action ${
                    isWishlisted ? "btn-wishlist-active" : "btn-wishlist"
                  }`}
                  onClick={toggleWishlist}
                >
                  <i
                    className={`bi ${
                      isWishlisted ? "bi-heart-fill" : "bi-heart"
                    } me-2`}
                  ></i>
                  {isWishlisted ? "WISHLISTED" : "WISHLIST"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: SIDEBAR ADS */}
        <div className="col-lg-2">
          <div className="side-ad-container shadow-sm">
            <VerticalAdsDis />
          </div>
        </div>
      </div>
    </div>
  );
}
